import React, { useState, useMemo } from "react";
import { useQuery, gql } from "@apollo/client";
import { useParams, useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";

import Loader from "./Loader";
import "./UserDashboard.css";

/* =======================
   GraphQL Query
======================= */

const GET_USER_DASHBOARD = gql`
  query getUserDashboard($data: UserDashboardInput!) {
    getUserDashboard(data: $data) {
      status
      message
      totalServices {
        id
        bookingId
        bookingDate
        timeSlot
        status
        price
        address
        couponCode
        couponDiscount
        codCharges
        verificationStatus
        orderDetail {
          id
          name
        }
      }
    }
  }
`;

const UserDashboard = () => {
  const { userId } = useParams();
  const location = useLocation();
  const { userData } = location.state || {};
  const navigate = useNavigate();

  const [searchBookingId, setSearchBookingId] = useState("");

  /* =======================
     Query (HOOK #1)
  ======================= */
  const { loading, error, data } = useQuery(GET_USER_DASHBOARD, {
    variables: { data: { userId } },
    skip: !userId,
    fetchPolicy: "network-only",
  });

  /* =======================
     Derived Data (HOOK #2)
  ======================= */
  const totalServices = data?.getUserDashboard?.totalServices || [];

  const firstVerifiedBookingDate = useMemo(() => {
    if (!totalServices.length) return null;

    const verified = totalServices
      .filter(
        (s) =>
          s.verificationStatus === "VERIFIED" ||
          s.verificationStatus === "PAYMENT_VERIFICATION_PENDING"
      )
      .sort((a, b) => new Date(a.bookingDate) - new Date(b.bookingDate));

    return verified[0]?.bookingDate || null;
  }, [totalServices]);

  const filteredServices = useMemo(() => {
    return totalServices.filter((service) =>
      service.bookingId?.toLowerCase().includes(searchBookingId.toLowerCase())
    );
  }, [totalServices, searchBookingId]);

  /* =======================
     EARLY RETURNS (SAFE)
  ======================= */
  if (loading) return <Loader />;
  if (error) return <p className="text-red-500">Error: {error.message}</p>;

  /* =======================
     UI
  ======================= */

  return (
    <div className="min-h-screen bg-[#121212] p-6 text-white space-y-6">
      <button
        onClick={() => navigate(-1)}
        className="bg-white text-black px-4 py-2 rounded-md"
      >
        ← Back to Customers
      </button>
      {/* USER SUMMARY */}
      <div className="bg-[#1f1f1f] p-5 rounded-lg flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold">{userData?.fullName || "—"}</h1>
          <p className="text-gray-300">📞 {userData?.contactNumber || "—"}</p>
        </div>

        <div>
          <p className="text-gray-400 text-sm">First Verified Booking</p>
          <p className="font-medium">
            {firstVerifiedBookingDate
              ? new Date(firstVerifiedBookingDate).toLocaleDateString()
              : "—"}
          </p>
        </div>
      </div>

      {/* FILTER */}
      <div className="bg-[#1f1f1f] p-4 rounded-lg">
        <input
          type="text"
          placeholder="Search by Booking ID"
          value={searchBookingId}
          onChange={(e) => setSearchBookingId(e.target.value)}
          className="p-2 rounded-md w-64 text-black"
        />
      </div>

      {/* BOOKINGS */}
      <div className="space-y-4">
        {filteredServices.length === 0 && (
          <p className="text-gray-400">No bookings found.</p>
        )}

        {filteredServices.map((service) => (
          <div
            key={service.id}
            className="bg-[#1f1f1f] p-5 rounded-lg space-y-3"
          >
            <div className="flex flex-wrap justify-between items-center gap-3">
              <h3 className="font-semibold">Booking ID: {service.bookingId}</h3>

              <span className="px-3 py-1 text-xs rounded-full bg-yellow-400 text-black">
                {service.status}
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
              <p>
                📅 <strong>Date:</strong>{" "}
                {service.bookingDate
                  ? new Date(service.bookingDate).toLocaleDateString()
                  : "—"}
              </p>
              <p>
                ⏰ <strong>Time:</strong> {service.timeSlot || "—"}
              </p>
              <p>
                💰 <strong>Price:</strong> ₹{service.price}
              </p>
            </div>

            {(service.couponCode || service.couponDiscount) && (
              <div className="text-sm text-green-400">
                🎟 Coupon: {service.couponCode || "—"} ( ₹
                {service.couponDiscount || 0} off)
              </div>
            )}

            <p className="text-sm text-gray-300">
              📍 <strong>Address:</strong> {service.address || "—"}
            </p>

            <div className="flex flex-wrap gap-2">
              {service.orderDetail?.length > 0 ? (
                service.orderDetail.map((d) => (
                  <span
                    key={d.id}
                    className="bg-[#2a2a2a] px-3 py-1 rounded text-xs"
                  >
                    {d.name}
                  </span>
                ))
              ) : (
                <span className="text-gray-400 text-sm">No services</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default UserDashboard;
